<template>
    <modal size="lg" :height-max="true" @close="reset(); $emit('close')" ref="modal" :is-closeable="isCloseable" id="editEarningPositionsModal" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title">Edit {{ this.step == 'default' ? 'Paygrid Defaults' : 'Positions' }}</h3>
        </template>

        <slot>
            <div class="mb-2">
                <div class="p-1 unpad-x bg-150 rounded">
                    <div v-if="earning.company"><span class="text-muted">Company: </span>{{ earning.company.name }}</div>
                    <div><span class="text-muted">Earning: </span>{{ earning.name || earning.earning_type.name }}</div>
                </div>
            </div>

            <div v-if="step == 'available'">
                <form-errors :errors="formErrors"/>

                <div class="mb-2">
                    <form-input
                        v-model="formData.default_for_all_positions"
                        :errors="errors.default_for_all_positions"
                        type="checkbox"
                        label="Available to all positions"
                        helper-text="Every position at this company can use this earning, including positions added later."
                    />
                </div>

                <template v-if="!formData.default_for_all_positions">
                    <p class="mb-2">Select the positions that can use this earning.</p>

                    <position-selector v-if="positionsLoaded" :all-positions="allCompanyPositions" :companies="client.companies" v-model="formData.positions"/>
                </template>
            </div>

            <div v-if="step == 'default'">
                <form-errors :errors="formErrors"/>

                <!-- An earning available to all positions keeps no per-position records,
                     so its paygrid default is all positions or none. -->
                <form-input
                    v-if="formData.default_for_all_positions"
                    v-model="formData.always_show_on_paygrid"
                    type="checkbox"
                    label="Show on the paygrid by default"
                    :errors="errors.always_show_on_paygrid"
                    helper-text="New jobs at every position are pre-filled with this earning. Positions cannot be chosen individually while this earning is available to all of them."
                />

                <template v-else>
                    <p class="mb-2">Select the positions that will use this earning by default.</p>

                    <position-selector v-if="positionsLoaded" :all-positions="selectedAvailablePositions" :companies="client.companies" v-model="formData.default_positions" :allPositionsLabel="'Available Positions'"/>
                </template>
            </div>
        </slot>

        <template v-slot:footer>
            <button-row stacked>
                <button v-if="step == 'available'" type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>
                <button v-else-if="step == 'default'" type="button" class="btn btn-outline-primary" @click.prevent="step = 'available'">
                    Back
                </button>
                <button v-if="step == 'available'" type="button" class="btn btn-primary" @click.prevent="step = 'default'">
                    Continue
                </button>
                <button v-else-if="step == 'default'" type="button" class="btn btn-primary" @click.prevent="onSubmit()">
                    Save
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import PositionSelector from '@/pages/client/Settings/components/PositionSelector'

export default {
    props: ['client', 'earning'],
    mixins: [FormMixin, ModalMixin],
    components: {PositionSelector, },
    data() {
        return {
            step: 'available',
            formData: this.makeFormData(),
            allPositions: [],
            positionsLoaded: false,
        }
    },
    watch: {
        earning() {
            this.reset()
            this.step = 'available'
        }
    },
    mounted() {
        this.getPositions()
    },
    computed: {
        allCompanyPositions() {
            return this.allPositions.filter(p => p.company_id == this.earning.company_id)
        },
        selectedAvailablePositions() {
            return this.allCompanyPositions.filter(p => this.formData.positions.includes(p.id))
        }
    },
    methods: {
        getPositions() {
            this.$api.get(`/clients/${this.client.id}/positions`).then(resp => {
                this.allPositions = resp
                this.allPositions.sort((a, b) => {return a.terminal_value.toLowerCase() > b.terminal_value.toLowerCase() ? 1 : -1})
                this.positionsLoaded = true
            }).catch((errors) => {
                this.$bus.showError(errors.__all__)
            })
        },
        makeFormData() {
            const data = {
                positions: [],
                default_positions: [],
                default_for_all_positions: !!this.earning.default_for_all_positions,
                always_show_on_paygrid: !!this.earning.always_show_on_paygrid,
            }
            this.earning.position_rels.forEach(rel => {
                data.positions.push(rel.position.id)
                if (rel.is_default) {
                    data.default_positions.push(rel.position.id)
                }
            })
            return data
        },
        getFormData() {
            const allPositions = this.formData.default_for_all_positions
            return {
                default_for_all_positions: allPositions,
                // The two paygrid-default levers are mutually exclusive: an
                // all-positions earning has no per-position records to hang a default
                // on, and an explicitly scoped one has nothing else.
                always_show_on_paygrid: allPositions && this.formData.always_show_on_paygrid,
                positions: allPositions ? [] : this.formData.positions,
                default_positions: allPositions
                    ? []
                    : this.formData.default_positions.filter(pId => this.formData.positions.includes(pId)),
            }
        },
        onSubmit() {
            this.$store.dispatch('START_LOADING')
            return this.$api.put(`/clients/${this.client.id}/settings/earning-types/${this.earning.id}/positions`, this.getFormData()).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('updated', resp)
                this.close()
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.formErrors = errors.__all__
                this.onError()
            })
        }
    }
}
</script>
